import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import FormBuilder from '../../components/forms/FormBuilder';

// Helper to count active items per stage
const useItemCounts = (workflowId) => {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    if (!workflowId) return;
    api.getWorkflowItems({ workflow_id: workflowId, status: 'active', limit: 500 })
      .then(data => {
        const items = data.items || data || [];
        const map = {};
        for (const item of items) {
          map[item.current_stage_id] = (map[item.current_stage_id] || 0) + 1;
        }
        setCounts(map);
      })
      .catch(() => {});
  }, [workflowId]);
  return counts;
};

const STAGE_COLORS = [
  '#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#ec4899',
  '#06b6d4', '#f97316', '#14b8a6', '#6366f1', '#059669', '#6b7280',
];

// Steps are numbered by order. The only type a person sets is "Finish"
// (items end there). The start is always the first step that isn't a
// finish — the engine begins every new item at the lowest 'start' stage.
const isFinish = (s) => s.stage_type === 'terminal';
function normalizedType(stages, idx) {
  if (isFinish(stages[idx])) return 'terminal';
  return stages.findIndex(s => !isFinish(s)) === idx ? 'start' : 'active';
}

const ROLES = ['admin', 'super_admin', 'sales', 'dispatcher', 'driver', 'maintenance'];
const ROUTING_MODES = [
  { value: 'role', label: 'Role' },
  { value: 'position', label: 'Position' },
  { value: 'supervisor', label: "Submitter's Supervisor" },
  // Client-only mode. Saved as owner_routing 'role' with no role plus the
  // picked users in workflow_stage_owners — resolveStageOwners() checks
  // picked users first, so they are exactly who owns the step.
  { value: 'people', label: 'Specific people' },
];

const CONDITION_OPERATORS = [
  { value: '', label: 'Always spawn (no condition)' },
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'in', label: 'is one of (comma-separated)' },
];

// ── Settings-card option lists (mirror the wizard so both surfaces agree) ──
const MODULES = [
  { value: 'sales', label: 'Sales' },
  { value: 'maintenance', label: 'Truck Maintenance' },
  { value: 'dispatch', label: 'Dispatch' },
  { value: 'admin', label: 'Admin' },
  { value: 'custom', label: 'Other / Custom' },
];

const VISIBILITY_OPTIONS = [
  { value: 'submitter_only', label: 'Submitter only' },
  { value: 'reports_to', label: 'Reports-to chain' },
  { value: 'team', label: "Submitter's team" },
  { value: 'module', label: 'Anyone with module access' },
  { value: 'all', label: 'Everyone' },
];

const PERMISSION_TIERS = [
  { value: 'driver', label: 'Driver' },
  { value: 'sales', label: 'Sales' },
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

// Spine statuses a stage can mirror when the template is attached to Work
// Orders (trigger_source_type='work_order') — matches the wizard's list.
const WO_SPINE_OPTIONS = [
  { value: '',            label: '— No sync —' },
  { value: 'open',        label: 'Open' },
  { value: 'assigned',    label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed' },
  { value: 'closed',      label: 'Closed' },
];

// Actions the SLA service actually executes (workflowSlaService.js)
const ESCALATION_ACTIONS = [
  { value: 'remind_owner', label: 'Remind owner(s)' },
  { value: 'notify_supervisor', label: 'Notify supervisor(s)' },
  { value: 'notify_admin', label: 'Notify admins' },
];

const EMPTY_GATE_SCHEMA = {
  version: 1,
  title: '',
  sections: [{ title: 'Required to advance', description: '', fields: [] }],
};

function gateFieldCount(schema) {
  if (!schema || !Array.isArray(schema.sections)) return 0;
  return schema.sections.reduce((acc, sec) => acc + (Array.isArray(sec.fields) ? sec.fields.length : 0), 0);
}

// "Property Site Inspection" → "property_site_inspection" (trigger key)
function slugifyName(name) {
  return String(name || '')
    .trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64) || 'workflow';
}

function safeJson(v) {
  if (Array.isArray(v)) return v;
  if (typeof v !== 'string' || !v) return null;
  try { return JSON.parse(v); } catch { return null; }
}

// Raw-JSON editor for LEGACY flat-array gates (Refund / Take Back etc). Those
// pre-date FormSchema; rewriting them into the builder would risk live
// workflows, so power users edit the JSON directly instead.
function GateJsonEditor({ value, onSave }) {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [err, setErr] = useState('');
  return (
    <div>
      <div style={{ fontSize: 11, color: '#92400e', marginBottom: 6 }}>
        Legacy field-list gate (pre-form-builder). Edit the JSON directly — array of{' '}
        <code>{'{ key, label, type, required }'}</code> objects.
      </div>
      <textarea
        className="sales-input"
        value={text}
        onChange={e => { setText(e.target.value); setErr(''); }}
        rows={8}
        style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
      />
      {err && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{err}</div>}
      <button className="btn" style={{ marginTop: 6, fontSize: 12, fontWeight: 600, backgroundColor: '#D4A843', color: '#1a1a2e' }}
        onClick={() => {
          try {
            const parsed = JSON.parse(text);
            if (!Array.isArray(parsed)) throw new Error('Must be a JSON array');
            onSave(parsed);
            setErr('');
          } catch (e) {
            setErr(e.message);
          }
        }}>
        Apply JSON
      </button>
    </div>
  );
}

export default function WorkflowStageEditor() {
  const { id: workflowId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [stages, setStages] = useState([]);
  const [transitions, setTransitions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [expandedPicker, setExpandedPicker] = useState(null); // stage index
  const [pickerQuery, setPickerQuery] = useState('');
  // Where new items begin as loaded from the server — so reordering or
  // marking finishes can warn before the start step silently changes.
  const [loadedStartId, setLoadedStartId] = useState(null);
  const [expandedSpawns, setExpandedSpawns] = useState(null); // stage index for the "Spawns on Enter" panel
  const [spawnTypes, setSpawnTypes] = useState([]); // [{key, label, description, requires_target, category}]
  const [spawnTemplates, setSpawnTemplates] = useState([]); // available child templates for workflow_item spawns
  const itemCounts = useItemCounts(workflowId);

  // Settings card (identity / form / trigger / permissions) — one-page editing
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [forms, setForms] = useState([]);
  const [expandedEscalations, setExpandedEscalations] = useState(null); // stage index
  const [expandedGate, setExpandedGate] = useState(null); // transition index

  const attachTo = settings?.attach_to || '';

  const loadData = useCallback(async () => {
    try {
      const data = await api.getWorkflowTemplate(workflowId);
      setTemplate(data);
      // Hydrate the settings card from the template row
      setSettings({
        name: data.name || '',
        description: data.description || '',
        module: data.module || '',
        button_label: data.landing_page_button_label || '',
        form_id: data.form_id || null,
        visibility_scope: data.visibility_scope || 'submitter_only',
        tiers: safeJson(data.can_submit_permission_tiers) || [],
        position_ids: safeJson(data.can_submit_position_ids) || [],
        attach_to: data.trigger_source_type === 'work_order' ? 'work_order' : '',
        _orig_trigger: data.trigger_source_type || '',
      });
      // Map specific_owners from server into specific_owner_ids for local state;
      // normalize spawn rows (always an array, always with explicit condition fields)
      const stagesWithOwnerIds = (data.stages || []).map(s => ({
        ...s,
        escalations: Array.isArray(s.escalations) ? s.escalations.map(e => ({
          trigger_pct: e.trigger_pct ?? 100,
          action: e.action || 'remind_owner',
          notify_role: e.notify_role || null,
        })) : [],
        specific_owner_ids: s.specific_owners?.map(o => o.user_id) || [],
        owner_routing: s.specific_owners?.length > 0 ? 'people' : (s.owner_routing || 'role'),
        spawns: Array.isArray(s.spawns) ? s.spawns.map(sp => ({
          id: sp.id,
          spawn_type: sp.spawn_type || '',
          spawn_target_id: sp.spawn_target_id || null,
          config_json: sp.config_json || null,
          condition_field: sp.condition_field || '',
          condition_operator: sp.condition_operator || '',
          condition_value: sp.condition_value || '',
          position: sp.position || 0,
          is_active: sp.is_active === 0 || sp.is_active === false ? 0 : 1,
          hold_mode: sp.hold_mode === 'hold' ? 'hold' : 'independent',
          holds_stage_id: sp.holds_stage_id || null,
        })) : [],
      }));
      setStages(stagesWithOwnerIds);
      const loadedStart = stagesWithOwnerIds
        .filter(s => s.stage_type === 'start')
        .sort((a, b) => a.position - b.position)[0];
      setLoadedStartId(loadedStart ? loadedStart.id : null);
      setTransitions(data.transitions || []);
    } catch (err) {
      console.error('Failed to load template:', err);
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load all active users, positions, and forms (for the settings card picker)
  useEffect(() => {
    api.getActiveUsers().then(setAllUsers).catch(() => {});
    api.getPositions().then(setPositions).catch(() => {});
    api.listForms().then(d => setForms(Array.isArray(d) ? d : (d?.forms || []))).catch(() => {});
    api.getWorkflowSpawnTypes().then(d => {
      setSpawnTypes(d?.spawn_types || []);
      setSpawnTemplates(d?.available_templates || []);
    }).catch(() => {});
  }, []);

  const updateSettings = (patch) => {
    setSettings(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  // Escalation helpers — same shape the SLA service reads
  const addEscalation = (stageIdx) => {
    setStages(prev => prev.map((s, i) => i === stageIdx
      ? { ...s, escalations: [...(s.escalations || []), { trigger_pct: 100, action: 'remind_owner', notify_role: null }] }
      : s));
    setDirty(true);
  };
  const updateEscalation = (stageIdx, escIdx, field, value) => {
    setStages(prev => prev.map((s, i) => i === stageIdx
      ? { ...s, escalations: (s.escalations || []).map((e, j) => j === escIdx ? { ...e, [field]: value } : e) }
      : s));
    setDirty(true);
  };
  const removeEscalation = (stageIdx, escIdx) => {
    setStages(prev => prev.map((s, i) => i === stageIdx
      ? { ...s, escalations: (s.escalations || []).filter((_, j) => j !== escIdx) }
      : s));
    setDirty(true);
  };

  const updateTransitionGate = (transIdx, nextSchema) => {
    setTransitions(prev => prev.map((t, i) => i === transIdx ? { ...t, required_fields: nextSchema } : t));
    setDirty(true);
  };

  // Persist the settings card via the same PUT the wizard uses. Trigger rules
  // match the wizard: attach → 'work_order'; detach → slug convention;
  // otherwise leave the historical trigger untouched.
  const saveSettings = async () => {
    if (!settings) return;
    if (!settings.name.trim()) throw new Error('Workflow name is required');
    const trigger =
      settings.attach_to === 'work_order' ? 'work_order'
      : settings._orig_trigger === 'work_order'
        ? `${settings.module || 'workflow'}.${slugifyName(settings.name)}`
        : undefined;
    const payload = {
      name: settings.name.trim(),
      description: settings.description || null,
      module: settings.module || null,
      landing_page_button_label: settings.button_label || null,
      form_id: settings.form_id || null,
      visibility_scope: settings.visibility_scope || 'submitter_only',
      can_submit_permission_tiers: settings.tiers.length > 0 ? settings.tiers : null,
      can_submit_position_ids: settings.position_ids.length > 0 ? settings.position_ids : null,
    };
    if (trigger !== undefined) payload.trigger_source_type = trigger;
    await api.updateWorkflowTemplate(workflowId, payload);
  };

  // Spawn helpers — add / update / remove spawn rows on a given stage.
  const addSpawn = (stageIdx) => {
    setStages(prev => prev.map((s, i) => {
      if (i !== stageIdx) return s;
      const newSpawns = [...(s.spawns || []), {
        spawn_type: spawnTypes.find(t => t.key === 'workflow_item')?.key || spawnTypes[0]?.key || '',
        hold_mode: 'independent',
        holds_stage_id: null,
        spawn_target_id: null,
        config_json: null,
        condition_field: '',
        condition_operator: '',
        condition_value: '',
        position: (s.spawns || []).length,
        is_active: 1,
      }];
      return { ...s, spawns: newSpawns };
    }));
    setDirty(true);
  };

  const updateSpawn = (stageIdx, spawnIdx, field, value) => {
    setStages(prev => prev.map((s, i) => {
      if (i !== stageIdx) return s;
      const spawns = (s.spawns || []).map((sp, j) => j === spawnIdx ? { ...sp, [field]: value } : sp);
      return { ...s, spawns };
    }));
    setDirty(true);
  };

  const removeSpawn = (stageIdx, spawnIdx) => {
    setStages(prev => prev.map((s, i) => {
      if (i !== stageIdx) return s;
      const spawns = (s.spawns || []).filter((_, j) => j !== spawnIdx).map((sp, j) => ({ ...sp, position: j }));
      return { ...s, spawns };
    }));
    setDirty(true);
  };

  const addStage = () => {
    const maxPos = stages.length > 0 ? Math.max(...stages.map(s => s.position)) + 1 : 0;
    setStages([...stages, {
      _new: true, name: '', color: STAGE_COLORS[stages.length % STAGE_COLORS.length],
      position: maxPos, stage_type: 'active', sla_hours: null, default_owner_role: null,
      owner_routing: 'role', default_owner_position_id: null,
    }]);
    setDirty(true);
  };

  const updateStage = (idx, field, value) => {
    setStages(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
    setDirty(true);
  };

  const removeStage = (idx) => {
    if (!window.confirm('Remove this stage?')) return;
    const removed = stages[idx];
    setStages(prev => prev.filter((_, i) => i !== idx));
    // Remove transitions referencing this stage
    if (removed.id) {
      setTransitions(prev => prev.filter(t => t.from_stage_id !== removed.id && t.to_stage_id !== removed.id));
    }
    setDirty(true);
  };

  const moveStage = (idx, direction) => {
    const newStages = [...stages];
    const [item] = newStages.splice(idx, 1);
    newStages.splice(idx + direction, 0, item);
    // Update positions
    newStages.forEach((s, i) => s.position = i);
    setStages(newStages);
    setDirty(true);
  };

  const toggleTransition = (fromId, toId) => {
    const exists = transitions.find(t =>
      (t.from_stage_id || t._fromIdx) === fromId && (t.to_stage_id || t._toIdx) === toId
    );
    if (exists) {
      setTransitions(prev => prev.filter(t =>
        !((t.from_stage_id || t._fromIdx) === fromId && (t.to_stage_id || t._toIdx) === toId)
      ));
    } else {
      setTransitions(prev => [...prev, { from_stage_id: fromId, to_stage_id: toId, required_fields: null }]);
    }
    setDirty(true);
  };

  const handleSave = async (forceMoveTo) => {
    // Guard: if called from onClick, React passes a SyntheticEvent as the first
    // arg. Ignore anything that isn't a plain object (the retry path passes a
    // real force_move_to map). This is what caused the "JSON.stringify cannot
    // serialize cyclic structures" error.
    if (forceMoveTo && (typeof forceMoveTo !== 'object' || forceMoveTo.nativeEvent || forceMoveTo.target)) {
      forceMoveTo = undefined;
    }
    // Validate
    for (const s of stages) {
      if (!s.name.trim()) return alert('All stages must have a name');
    }
    if (!stages.find(s => !isFinish(s))) return alert('At least one step must not be a Finish step — new items need somewhere to start');
    if (!stages.find(s => isFinish(s))) return alert('Mark at least one step as Finish so items can be completed');
    const noPeople = stages.find(s => s.owner_routing === 'people' && !(s.specific_owner_ids || []).length);
    if (noPeople) return alert(`"${noPeople.name || 'A step'}" is set to Specific people but nobody is picked`);

    setSaving(true);
    try {
      // Save the settings card first (identity / form / trigger / permissions)
      await saveSettings();

      // Save stages (optionally with force_move_to mapping)
      const payload = stages.map((s, i) => ({
        id: s._new ? undefined : s.id,
        name: s.name.trim(),
        color: s.color,
        position: i,
        stage_type: normalizedType(stages, i),
        sla_hours: s.sla_hours ? Number(s.sla_hours) : null,
        default_owner_role: s.owner_routing === 'people' ? null : (s.default_owner_role || null),
        owner_routing: s.owner_routing === 'people' ? 'role' : (s.owner_routing || 'role'),
        default_owner_position_id: s.owner_routing === 'people' ? null : (s.default_owner_position_id || null),
        maps_to_status: s.maps_to_status || null,
        specific_owner_ids: s.specific_owner_ids || [],
        spawns: (s.spawns || []).filter(sp => sp.spawn_type).map((sp, j) => ({
          spawn_type: sp.spawn_type,
          spawn_target_id: sp.spawn_target_id || null,
          config_json: sp.config_json || null,
          condition_field: sp.condition_field || null,
          condition_operator: sp.condition_operator || null,
          condition_value: sp.condition_value === '' ? null : sp.condition_value,
          hold_mode: sp.hold_mode === 'hold' ? 'hold' : 'independent',
          holds_stage_id: sp.hold_mode === 'hold' ? (sp.holds_stage_id || null) : null,
          position: j,
          is_active: sp.is_active === 0 || sp.is_active === false ? 0 : 1,
        })),
      }));
      const savedStages = await api.saveWorkflowStages(workflowId, payload, forceMoveTo);

      // Map old/new stage indices to saved IDs
      const stageIdMap = {};
      stages.forEach((s, i) => {
        if (s.id) stageIdMap[s.id] = savedStages[i]?.id || s.id;
      });

      // Save escalations per stage (replace strategy server-side)
      for (let i = 0; i < stages.length; i++) {
        const sid = savedStages[i]?.id || stages[i].id;
        if (!sid) continue;
        await api.saveStageEscalations(sid, (stages[i].escalations || []).map(e => ({
          trigger_pct: Number(e.trigger_pct) || 100,
          action: e.action || 'remind_owner',
          notify_role: e.notify_role || null,
        })));
      }

      // Save transitions (map to real IDs)
      const mappedTransitions = transitions.map(t => ({
        from_stage_id: stageIdMap[t.from_stage_id] || t.from_stage_id,
        to_stage_id: stageIdMap[t.to_stage_id] || t.to_stage_id,
        required_fields: t.required_fields,
      })).filter(t => t.from_stage_id && t.to_stage_id);

      await api.saveWorkflowTransitions(workflowId, mappedTransitions);
      setDirty(false);
      await loadData();
    } catch (err) {
      // Check if the failure was "stages have active items" — offer to force-move + retry
      const body = err.responseBody || {};
      if (body.blocked && Array.isArray(body.blocked) && body.blocked.length > 0) {
        const targets = body.available_targets || [];
        if (targets.length === 0) {
          alert(err.message || 'No remaining stages to move items to.');
        } else {
          // Build a force_move_to map by asking the user for each blocked stage
          const moveMap = {};
          let cancelled = false;
          for (const b of body.blocked) {
            const list = targets.map((t, i) => `${i + 1}) ${t.name}`).join('\n');
            const pick = window.prompt(
              `"${b.name}" has ${b.count} item(s) still on it.\n\n` +
              `Move them to which stage?\n\n${list}\n\nEnter a number, or Cancel to abort.`
            );
            if (pick === null || pick === '') { cancelled = true; break; }
            const idx = Number(pick) - 1;
            if (Number.isNaN(idx) || idx < 0 || idx >= targets.length) {
              alert('Invalid choice — aborting.');
              cancelled = true;
              break;
            }
            moveMap[b.id] = targets[idx].id;
          }
          if (!cancelled) {
            if (window.confirm(`Force delete? This will move items as specified, then delete the stage(s).`)) {
              setSaving(false);
              await handleSave(moveMap); // retry with force map
              return;
            }
          }
        }
      } else {
        alert(err.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>;
  if (!template) return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Template not found</div>;

  // Attached-to-WO templates get one extra column: the spine mapping.
  const gridCols = attachTo === 'work_order'
    ? '28px 64px 2fr 100px 80px 140px 1.6fr 130px 28px'
    : '28px 64px 2fr 100px 80px 140px 1.6fr 28px';

  // The step new items begin at, and a warning if that differs from what's
  // saved (someone reordered steps or marked the old start as a Finish).
  const startIdx = stages.findIndex(st => !isFinish(st));
  const savedStart = stages.find(st => st.id && st.id === loadedStartId);
  const startMoved = savedStart && startIdx >= 0 && stages[startIdx].id !== loadedStartId;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
      <button onClick={() => navigate('/workflow/setup')} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
        ← Back to Workflows
      </button>

      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>
            {settings?.name || template.name}
          </h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>
            The whole workflow on one page — settings, stages, gates, transitions.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" onClick={async () => {
            // Validate first
            for (const s of stages) {
              if (!s.name.trim()) return alert('All stages must have a name');
            }
            const hasRouting = stages.some(s => s.default_owner_role || s.default_owner_position_id || s.owner_routing === 'supervisor' || (s.owner_routing === 'people' && s.specific_owner_ids?.length));
            if (!hasRouting) return alert('Set who is responsible (role, position, supervisor, or specific people) on at least one step first.');
            if (!window.confirm('This will SAVE your current stage settings, then re-assign owners on all active items. Continue?')) return;
            setReassigning(true);
            try {
              // Step 1: Save stages first so DB has the latest config
              await saveSettings();
              await api.saveWorkflowStages(workflowId, stages.map((s, i) => ({
                id: s._new ? undefined : s.id,
                name: s.name.trim(),
                color: s.color,
                position: i,
                stage_type: normalizedType(stages, i),
                sla_hours: s.sla_hours ? Number(s.sla_hours) : null,
                default_owner_role: s.owner_routing === 'people' ? null : (s.default_owner_role || null),
                owner_routing: s.owner_routing === 'people' ? 'role' : (s.owner_routing || 'role'),
                default_owner_position_id: s.owner_routing === 'people' ? null : (s.default_owner_position_id || null),
                maps_to_status: s.maps_to_status || null,
                specific_owner_ids: s.specific_owner_ids || [],
                spawns: (s.spawns || []).filter(sp => sp.spawn_type).map((sp, j) => ({
                  spawn_type: sp.spawn_type,
                  spawn_target_id: sp.spawn_target_id || null,
                  config_json: sp.config_json || null,
                  condition_field: sp.condition_field || null,
                  condition_operator: sp.condition_operator || null,
                  condition_value: sp.condition_value === '' ? null : sp.condition_value,
                  hold_mode: sp.hold_mode === 'hold' ? 'hold' : 'independent',
                  holds_stage_id: sp.hold_mode === 'hold' ? (sp.holds_stage_id || null) : null,
                  position: j,
                  is_active: sp.is_active === 0 || sp.is_active === false ? 0 : 1,
                })),
              })));
              // Step 2: Save transitions too
              const stageIdMap = {};
              stages.forEach((s, i) => { if (s.id) stageIdMap[s.id] = s.id; });
              const mappedTransitions = transitions.map(t => ({
                from_stage_id: stageIdMap[t.from_stage_id] || t.from_stage_id,
                to_stage_id: stageIdMap[t.to_stage_id] || t.to_stage_id,
                required_fields: t.required_fields,
              })).filter(t => t.from_stage_id && t.to_stage_id);
              await api.saveWorkflowTransitions(workflowId, mappedTransitions);
              // Step 3: Now reassign
              const result = await api.reassignWorkflowOwners(workflowId);
              const msg = `${result.reassigned}/${result.total} items updated.\n\n${(result.details || []).join('\n')}`;
              alert(msg);
              setDirty(false);
              await loadData();
            } catch (err) {
              alert(err.message || 'Failed');
            } finally {
              setReassigning(false);
            }
          }} disabled={reassigning}
            style={{ backgroundColor: '#fef3c7', color: '#92400e', fontWeight: 600 }}>
            {reassigning ? 'Saving & Applying...' : 'Save & Apply to Active Items'}
          </button>
          <button className="btn" onClick={addStage} style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}>
            + Add Stage
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !dirty}
            style={{ backgroundColor: dirty ? '#D4A843' : '#d1d5db', color: '#1a1a2e', fontWeight: 600 }}>
            {saving ? 'Saving...' : dirty ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </div>

      {/* Settings card — identity / form / trigger / permissions, all inline.
           Collapsed by default so the stages stay front-and-center; the
           summary chips tell you the config at a glance. */}
      {settings && (
        <div style={{ marginBottom: 20, backgroundColor: '#fff', borderRadius: 10, border: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setSettingsOpen(!settingsOpen)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
              {settingsOpen ? '▾' : '▸'} Workflow Settings
            </span>
            {!settingsOpen && (
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  MODULES.find(m => m.value === settings.module)?.label || 'No module',
                  settings.form_id ? (forms.find(f => f.id === settings.form_id)?.name || 'Form attached') : 'No form',
                  attachTo === 'work_order' ? '🔗 Work Orders' : null,
                  VISIBILITY_OPTIONS.find(v => v.value === settings.visibility_scope)?.label,
                  settings.tiers.length > 0 || settings.position_ids.length > 0
                    ? `Submit: ${[...settings.tiers, ...settings.position_ids.map(id => positions.find(p => p.id === id)?.name || id)].join(', ')}`
                    : 'Anyone submits',
                ].filter(Boolean).map((chip, i) => (
                  <span key={i} style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
                    backgroundColor: '#f3f4f6', color: '#4b5563',
                  }}>{chip}</span>
                ))}
              </span>
            )}
          </button>

          {settingsOpen && (
            <div style={{ padding: '4px 20px 18px', display: 'grid', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Name *</label>
                  <input className="sales-input" value={settings.name}
                    onChange={e => updateSettings({ name: e.target.value })} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Module</label>
                  <select className="sales-input" value={settings.module || ''}
                    onChange={e => updateSettings({ module: e.target.value })} style={{ width: '100%' }}>
                    <option value="">— None —</option>
                    {MODULES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Submit Button Label</label>
                  <input className="sales-input" value={settings.button_label} placeholder={`+ ${settings.name || 'Submit'}`}
                    onChange={e => updateSettings({ button_label: e.target.value })} style={{ width: '100%' }} />
                </div>
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Description</label>
                <input className="sales-input" value={settings.description} placeholder="Internal description for other admins"
                  onChange={e => updateSettings({ description: e.target.value })} style={{ width: '100%' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1.5fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Intake Form</label>
                  <select className="sales-input" value={settings.form_id || ''}
                    onChange={e => updateSettings({ form_id: e.target.value ? Number(e.target.value) : null })} style={{ width: '100%' }}>
                    <option value="">None (created by system or another module)</option>
                    {forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
                    Build new forms at Setup → Forms, then pick here.
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Attach to System Trigger</label>
                  <select className="sales-input" value={settings.attach_to || ''}
                    onChange={e => updateSettings({ attach_to: e.target.value })} style={{ width: '100%' }}>
                    <option value="">Standalone (submitted via form)</option>
                    <option value="work_order">Work Orders — card on every WO</option>
                  </select>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
                    Attaching unlocks the WO Status column on stages below.
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Who Can See Items</label>
                  <select className="sales-input" value={settings.visibility_scope}
                    onChange={e => updateSettings({ visibility_scope: e.target.value })} style={{ width: '100%' }}>
                    {VISIBILITY_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>
                  Who Can Submit <span style={{ fontWeight: 400, textTransform: 'none' }}>(none selected = anyone with module access)</span>
                </label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {PERMISSION_TIERS.map(t => {
                    const on = settings.tiers.includes(t.value);
                    return (
                      <button key={t.value} onClick={() => updateSettings({
                        tiers: on ? settings.tiers.filter(x => x !== t.value) : [...settings.tiers, t.value],
                      })} className="btn" style={{
                        fontSize: 12, padding: '5px 12px', fontWeight: 600, borderRadius: 6,
                        backgroundColor: on ? '#D4A843' : '#f3f4f6',
                        color: on ? '#1a1a2e' : '#6b7280',
                        border: on ? '1px solid #D4A843' : '1px solid #e5e7eb',
                      }}>{t.label}</button>
                    );
                  })}
                  <span style={{ alignSelf: 'center', fontSize: 11, color: '#9ca3af' }}>| positions:</span>
                  {positions.map(p => {
                    const on = settings.position_ids.includes(p.id);
                    return (
                      <button key={p.id} onClick={() => updateSettings({
                        position_ids: on ? settings.position_ids.filter(x => x !== p.id) : [...settings.position_ids, p.id],
                      })} className="btn" style={{
                        fontSize: 12, padding: '5px 12px', fontWeight: 600, borderRadius: 6,
                        backgroundColor: on ? '#3F66A0' : '#f3f4f6',
                        color: on ? '#fff' : '#6b7280',
                        border: on ? '1px solid #3F66A0' : '1px solid #e5e7eb',
                      }}>{p.name}</button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {startMoved && (
        <div style={{
          marginBottom: 12, padding: '10px 14px', borderRadius: 10, fontSize: 13,
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
        }}>
          New items currently start at <strong>{savedStart.name}</strong>. After you save they'll
          start at <strong>Step {startIdx + 1} · {stages[startIdx].name || 'Untitled'}</strong>.
          Move "{savedStart.name}" back to the top to keep things as they are.
        </div>
      )}

      {/* Column Headers */}
      {stages.length > 0 && (() => {
        const hdr = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6 };
        return (
          <div className="wf-stage-head" style={{
            display: 'grid', gridTemplateColumns: gridCols,
            gap: 16, alignItems: 'center',
            padding: '0 20px 8px 24px',
          }}>
            <div /> {/* reorder spacer */}
            <div style={{ ...hdr, textAlign: 'center' }}>Step</div>
            <div style={hdr}>Stage Name</div>
            <div style={hdr}>Color</div>
            <div style={hdr}>SLA (hrs)</div>
            <div style={hdr}>Route To</div>
            <div style={hdr}>Target</div>
            {attachTo === 'work_order' && <div style={hdr}>WO Status</div>}
            <div /> {/* delete spacer */}
          </div>
        );
      })()}

      {/* Stages List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {stages.map((stage, idx) => (
          <React.Fragment key={stage.id || `new-${idx}`}>
          <div className="wf-stage-row" style={{
            display: 'grid', gridTemplateColumns: gridCols,
            gap: 16, alignItems: 'center',
            padding: '14px 20px', backgroundColor: '#fff', borderRadius: 10,
            border: '1px solid #e5e7eb',
            borderLeft: `4px solid ${stage.color}`,
          }}>
            {/* Reorder buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              <button onClick={() => moveStage(idx, -1)} disabled={idx === 0}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: idx === 0 ? '#d1d5db' : '#6b7280', padding: 0 }}>▲</button>
              <button onClick={() => moveStage(idx, 1)} disabled={idx === stages.length - 1}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: idx === stages.length - 1 ? '#d1d5db' : '#6b7280', padding: 0 }}>▼</button>
            </div>

            {/* Step number. Start is automatic; Finish is the one toggle. */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{idx + 1}</span>
              {idx === startIdx ? (
                <span title="New items begin here" style={{
                  fontSize: 10, fontWeight: 700, color: '#059669',
                  textTransform: 'uppercase', letterSpacing: 0.4,
                }}>Start</span>
              ) : (
                <button type="button"
                  onClick={() => updateStage(idx, 'stage_type', isFinish(stage) ? 'active' : 'terminal')}
                  title={isFinish(stage)
                    ? 'Items end here. Click to make this a working step.'
                    : 'Click to make this a Finish step — items end here (Paid, Denied, Completed…)'}
                  style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, cursor: 'pointer',
                    textTransform: 'uppercase', letterSpacing: 0.3,
                    background: isFinish(stage) ? '#111827' : '#fff',
                    color: isFinish(stage) ? '#fff' : '#9ca3af',
                    border: `1px solid ${isFinish(stage) ? '#111827' : '#e5e7eb'}`,
                  }}>
                  {isFinish(stage) ? 'Finish' : '+ Finish'}
                </button>
              )}
            </div>

            {/* Stage Name + how many items sit in this step right now */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <input
                className="sales-input"
                placeholder="Stage name"
                value={stage.name}
                onChange={e => updateStage(idx, 'name', e.target.value)}
                style={{ width: '100%', fontWeight: 600, fontSize: 14 }}
              />
              {stage.id && itemCounts[stage.id] > 0 && (
                <span title="Items in this step right now" style={{
                  backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: 10, fontWeight: 700,
                  padding: '2px 6px', borderRadius: 10, whiteSpace: 'nowrap',
                }}>
                  {itemCounts[stage.id]}
                </span>
              )}
            </div>

            {/* Color picker */}
            <div className="wf-cell" data-label="Color" style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
              {STAGE_COLORS.slice(0, 6).map(c => (
                <div key={c} onClick={() => updateStage(idx, 'color', c)}
                  style={{
                    width: 18, height: 18, borderRadius: '50%', backgroundColor: c, cursor: 'pointer',
                    border: stage.color === c ? '2px solid #111' : '2px solid transparent',
                  }} />
              ))}
            </div>

            {/* SLA — the wf-wrap cells are invisible on desktop (display:
                contents) and become labelled rows on the phone. */}
            <div className="wf-cell wf-wrap" data-label="SLA (hrs)">
            <input
              className="sales-input"
              type="number"
              placeholder="Hours"
              value={stage.sla_hours || ''}
              onChange={e => updateStage(idx, 'sla_hours', e.target.value)}
              style={{ width: '100%', fontSize: 13 }}
              min="0"
            />
            </div>

            {/* Route To (mode) */}
            <div className="wf-cell wf-wrap" data-label="Route to">
            <select className="sales-input" value={stage.owner_routing || 'role'} onChange={e => {
              updateStage(idx, 'owner_routing', e.target.value);
              updateStage(idx, 'default_owner_role', null);
              updateStage(idx, 'default_owner_position_id', null);
              updateStage(idx, 'specific_owner_ids', []);
              if (e.target.value === 'people') { setPickerQuery(''); setExpandedPicker(idx); }
            }} style={{ width: '100%', fontSize: 12 }}>
              {ROUTING_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            </div>

            {/* Target (role select, position select, or "Auto" for supervisor) */}
            <div className="wf-cell" data-label="Target" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {(stage.owner_routing || 'role') === 'role' && (
                <select className="sales-input" value={stage.default_owner_role || ''} onChange={e => {
                  updateStage(idx, 'default_owner_role', e.target.value || null);
                  updateStage(idx, 'specific_owner_ids', []);
                }} style={{ width: '100%', fontSize: 12 }}>
                  <option value="">None</option>
                  {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                </select>
              )}
              {stage.owner_routing === 'position' && (
                <select className="sales-input" value={stage.default_owner_position_id || ''} onChange={e => {
                  updateStage(idx, 'default_owner_position_id', e.target.value ? Number(e.target.value) : null);
                }} style={{ width: '100%', fontSize: 12 }}>
                  <option value="">Select...</option>
                  {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
              {stage.owner_routing === 'supervisor' && (
                <span style={{ fontSize: 11, color: '#8b5cf6', fontWeight: 600 }}>Auto</span>
              )}
              {stage.owner_routing === 'people' && (() => {
                const n = stage.specific_owner_ids?.length || 0;
                return (
                  <button
                    onClick={() => { setPickerQuery(''); setExpandedPicker(expandedPicker === idx ? null : idx); }}
                    className="btn"
                    style={{
                      fontSize: 12, padding: '5px 10px', fontWeight: 600, borderRadius: 6, whiteSpace: 'nowrap',
                      backgroundColor: n > 0 ? '#d1fae5' : '#fef3c7',
                      color: n > 0 ? '#065f46' : '#92400e',
                      border: expandedPicker === idx ? '1px solid #D4A843' : '1px solid #e5e7eb',
                    }}
                  >
                    {n > 0 ? `${n} ${n === 1 ? 'person' : 'people'} ▾` : 'Pick people ▾'}
                  </button>
                );
              })()}
            </div>

            {/* WO spine mapping — entering this stage mirrors the mapped
                 status onto the work order (and vice versa) */}
            {attachTo === 'work_order' && (
              <div className="wf-cell wf-wrap" data-label="WO status">
              <select className="sales-input" value={stage.maps_to_status || ''}
                onChange={e => updateStage(idx, 'maps_to_status', e.target.value || null)}
                style={{ width: '100%', fontSize: 12 }}
                title="Moving a card into this stage sets the Work Order to this status. WO button clicks move the card here too.">
                {WO_SPINE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              </div>
            )}

            {/* Delete */}
            <button className="wf-del" onClick={() => removeStage(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, textAlign: 'center' }}>✕</button>
          </div>

          {/* Sub-tasks — compact toggle row sitting under the stage row */}
          <div style={{
            margin: '-4px 0 0 48px', padding: '6px 12px', fontSize: 12,
            display: 'flex', alignItems: 'center', gap: 10,
            color: '#6b7280',
          }}>
            <button
              onClick={() => setExpandedSpawns(expandedSpawns === idx ? null : idx)}
              className="btn"
              style={{
                fontSize: 11, padding: '4px 10px', fontWeight: 600,
                backgroundColor: (stage.spawns?.length > 0) ? '#fef3c7' : '#f3f4f6',
                color: (stage.spawns?.length > 0) ? '#92400e' : '#6b7280',
                border: expandedSpawns === idx ? '1px solid #D4A843' : '1px solid #e5e7eb',
                borderRadius: 6,
              }}
            >
              {expandedSpawns === idx ? '▾' : '▸'} Sub-tasks
              {stage.spawns?.length > 0 && (
                <span style={{ marginLeft: 6, backgroundColor: '#D4A843', color: '#1a1a2e', padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>
                  {stage.spawns.length}
                </span>
              )}
            </button>
            {stage.spawns?.length > 0 && expandedSpawns !== idx && (
              <span style={{ fontStyle: 'italic' }}>
                {stage.spawns.map(sp => {
                  const target = sp.spawn_type === 'workflow_item'
                    ? spawnTemplates.find(t => t.id === sp.spawn_target_id)?.name
                    : null;
                  const label = target || spawnTypes.find(t => t.key === sp.spawn_type)?.label || sp.spawn_type;
                  if (sp.hold_mode !== 'hold') return label;
                  const heldIdx = sp.holds_stage_id ? stages.findIndex(st => st.id === sp.holds_stage_id) : idx + 1;
                  return heldIdx > idx && heldIdx < stages.length ? `${label} (holds step ${heldIdx + 1})` : `${label} (holds next step)`;
                }).join(' · ')}
              </span>
            )}
            {stage.stage_type !== 'terminal' && (
              <button
                onClick={() => setExpandedEscalations(expandedEscalations === idx ? null : idx)}
                className="btn"
                style={{
                  fontSize: 11, padding: '4px 10px', fontWeight: 600,
                  backgroundColor: (stage.escalations?.length > 0) ? '#ede9fe' : '#f3f4f6',
                  color: (stage.escalations?.length > 0) ? '#5b21b6' : '#6b7280',
                  border: expandedEscalations === idx ? '1px solid #D4A843' : '1px solid #e5e7eb',
                  borderRadius: 6,
                }}
              >
                {expandedEscalations === idx ? '▾' : '▸'} ⏰ SLA Escalations
                {stage.escalations?.length > 0 && (
                  <span style={{ marginLeft: 6, backgroundColor: '#8b5cf6', color: '#fff', padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>
                    {stage.escalations.length}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Expanded SLA escalations editor */}
          {expandedEscalations === idx && (
            <div style={{
              marginTop: -4, padding: '14px 20px 14px 48px', backgroundColor: '#f5f3ff',
              borderRadius: '0 0 10px 10px', border: '1px solid #ddd6fe', borderTop: 'none',
            }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                Reminders and escalations for items in <b>{stage.name || 'this stage'}</b>, as a share of its SLA.
                Under 100% fires <i>before</i> the deadline (50% of a 48h SLA = 24h in, 24h left),
                100% = SLA just breached, 150% = one-and-a-half times the SLA, etc.
                {(!stage.escalations || stage.escalations.length === 0) && (
                  <span> With none configured, the defaults run: <b>100%</b> remind owner → <b>150%</b> notify supervisor → <b>200%</b> notify admins.</span>
                )}
              </div>
              {(stage.escalations || []).map((esc, escIdx) => (
                <div key={escIdx} style={{
                  display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px',
                  marginBottom: 6, backgroundColor: '#fff', border: '1px solid #ddd6fe', borderRadius: 8,
                }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>At</span>
                  <input className="sales-input" type="number" min="1" step="10"
                    value={esc.trigger_pct}
                    onChange={e => updateEscalation(idx, escIdx, 'trigger_pct', e.target.value)}
                    style={{ width: 80, fontSize: 12 }} />
                  <span style={{ fontSize: 12, color: '#6b7280' }}>% of SLA →</span>
                  <select className="sales-input" value={esc.action}
                    onChange={e => updateEscalation(idx, escIdx, 'action', e.target.value)}
                    style={{ fontSize: 12, minWidth: 180 }}>
                    {ESCALATION_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                  <button onClick={() => removeEscalation(idx, escIdx)}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, marginLeft: 'auto' }}>
                    ✕
                  </button>
                </div>
              ))}
              <button onClick={() => addEscalation(idx)} className="btn"
                style={{ fontSize: 12, fontWeight: 600, padding: '6px 12px', backgroundColor: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 6 }}>
                + Add Escalation
              </button>
              {!stage.sla_hours && (
                <div style={{ fontSize: 11, color: '#92400e', marginTop: 8 }}>
                  ⚠ This stage has no SLA hours set — escalations never fire without one.
                </div>
              )}
            </div>
          )}

          {/* Expanded Sub-tasks editor */}
          {expandedSpawns === idx && (
            <div style={{
              marginTop: -4, padding: '14px 20px 14px 48px', backgroundColor: '#fffbeb',
              borderRadius: '0 0 10px 10px', border: '1px solid #fde68a', borderTop: 'none',
            }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                When an item reaches <b>Step {idx + 1} · {stage.name || 'this step'}</b>, start these sub-tasks.
                <b> Hold</b> = the main path can't reach the step you pick until the sub-task is done.
                <b> Independent</b> = it runs on its own and never slows the main path.
              </div>
              {(stage.spawns || []).length === 0 && (
                <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 10px' }}>No sub-tasks yet.</p>
              )}
              {(stage.spawns || []).map((sp, spIdx) => {
                const typeDescriptor = spawnTypes.find(t => t.key === sp.spawn_type);
                return (
                  <div key={sp.id || `new-${spIdx}`} style={{
                    display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
                    padding: '10px 12px', marginBottom: 8, backgroundColor: '#fff',
                    border: '1px solid #fde68a', borderRadius: 8,
                  }}>
                    <select
                      className="sales-input"
                      value={sp.spawn_type}
                      onChange={e => updateSpawn(idx, spIdx, 'spawn_type', e.target.value)}
                      style={{ fontSize: 12, minWidth: 220 }}
                    >
                      {spawnTypes.length === 0 && <option value="">(loading types...)</option>}
                      {spawnTypes.map(t => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </select>
                    {typeDescriptor?.requires_target && (
                      <select
                        className="sales-input"
                        value={sp.spawn_target_id || ''}
                        onChange={e => updateSpawn(idx, spIdx, 'spawn_target_id', e.target.value ? Number(e.target.value) : null)}
                        style={{ fontSize: 12, minWidth: 200 }}
                      >
                        <option value="">Pick a workflow…</option>
                        {spawnTemplates.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    )}
                    <span style={{ fontSize: 11, color: '#6b7280' }}>only if</span>
                    <input
                      className="sales-input"
                      placeholder="field key (e.g. dropoff_type)"
                      value={sp.condition_field || ''}
                      onChange={e => updateSpawn(idx, spIdx, 'condition_field', e.target.value)}
                      style={{ fontSize: 12, width: 170 }}
                    />
                    <select
                      className="sales-input"
                      value={sp.condition_operator || ''}
                      onChange={e => updateSpawn(idx, spIdx, 'condition_operator', e.target.value)}
                      style={{ fontSize: 12, minWidth: 140 }}
                    >
                      {CONDITION_OPERATORS.map(op => (
                        <option key={op.value} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                    {sp.condition_operator && (
                      <input
                        className="sales-input"
                        placeholder={sp.condition_operator === 'in' ? 'val1, val2' : 'value'}
                        value={sp.condition_value || ''}
                        onChange={e => updateSpawn(idx, spIdx, 'condition_value', e.target.value)}
                        style={{ fontSize: 12, width: 120 }}
                      />
                    )}
                    <label style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="checkbox"
                        checked={sp.is_active !== 0 && sp.is_active !== false}
                        onChange={e => updateSpawn(idx, spIdx, 'is_active', e.target.checked ? 1 : 0)}
                      />
                      Active
                    </label>
                    <button
                      onClick={() => removeSpawn(idx, spIdx)}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16, marginLeft: 'auto' }}
                      title="Remove sub-task"
                    >
                      ✕
                    </button>
                    {typeDescriptor?.creates_item && (
                      <div style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                        <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                          {[
                            { v: 'independent', label: 'Independent', tip: 'Runs on its own road — never slows the main path' },
                            { v: 'hold', label: 'Hold', tip: 'The main path waits for this sub-task' },
                          ].map(opt => {
                            const on = (sp.hold_mode || 'independent') === opt.v;
                            return (
                              <button key={opt.v} type="button" title={opt.tip}
                                onClick={() => updateSpawn(idx, spIdx, 'hold_mode', opt.v)}
                                style={{
                                  fontSize: 11, fontWeight: 700, padding: '4px 10px', border: 'none', cursor: 'pointer',
                                  background: on ? (opt.v === 'hold' ? '#92400e' : '#3F66A0') : '#fff',
                                  color: on ? '#fff' : '#6b7280',
                                }}>
                                {opt.v === 'hold' ? '🔒 ' : ''}{opt.label}
                              </button>
                            );
                          })}
                        </div>
                        {sp.hold_mode === 'hold' && (
                          <label style={{ fontSize: 11, color: '#92400e', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            Main path can't reach
                            <select
                              className="sales-input"
                              value={sp.holds_stage_id || ''}
                              onChange={e => updateSpawn(idx, spIdx, 'holds_stage_id', e.target.value ? Number(e.target.value) : null)}
                              style={{ fontSize: 12, minWidth: 180 }}
                            >
                              <option value="">
                                {stages[idx + 1] ? `Step ${idx + 2} · ${stages[idx + 1].name || 'next step'} (next step)` : 'the next step'}
                              </option>
                              {stages.map((st, stIdx) => (stIdx > idx + 1 ? (
                                <option key={st.id || `new-${stIdx}`} value={st.id || ''} disabled={!st.id}>
                                  Step {stIdx + 1} · {st.name || 'Untitled'}{st.id ? '' : ' (save first)'}
                                </option>
                              ) : null))}
                            </select>
                            until this is done
                          </label>
                        )}
                      </div>
                    )}
                    {typeDescriptor?.description && (
                      <div style={{ flexBasis: '100%', fontSize: 11, color: '#92400e', marginTop: 2 }}>
                        ↳ {typeDescriptor.description}
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                onClick={() => addSpawn(idx)}
                className="btn"
                style={{
                  fontSize: 12, fontWeight: 600, padding: '6px 12px',
                  backgroundColor: '#D4A843', color: '#1a1a2e', border: 'none', borderRadius: 6,
                }}
              >
                + Add sub-task
              </button>
            </div>
          )}

          {/* Specific people picker — any active user, searchable */}
          {expandedPicker === idx && stage.owner_routing === 'people' && (() => {
            const selectedIds = stage.specific_owner_ids || [];
            const q = pickerQuery.trim().toLowerCase();
            const matches = allUsers
              .filter(u => !q || `${u.full_name || ''} ${u.username || ''}`.toLowerCase().includes(q))
              .sort((a, b) => (selectedIds.includes(b.id) - selectedIds.includes(a.id))
                || (a.full_name || a.username || '').localeCompare(b.full_name || b.username || ''));
            return (
              <div style={{
                marginTop: -4, padding: '12px 16px 12px 48px', backgroundColor: '#fafbfc',
                borderRadius: '0 0 10px 10px', border: '1px solid #e5e7eb', borderTop: 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                    Who is responsible for moving this step on?
                  </span>
                  <input
                    className="sales-input"
                    placeholder="Search people…"
                    value={pickerQuery}
                    onChange={e => setPickerQuery(e.target.value)}
                    style={{ fontSize: 12, padding: '4px 10px', width: 200 }}
                  />
                  {selectedIds.length > 0 && (
                    <button onClick={() => updateStage(idx, 'specific_owner_ids', [])} style={{
                      background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer',
                      fontSize: 11, fontWeight: 600, textDecoration: 'underline',
                    }}>
                      Clear
                    </button>
                  )}
                </div>
                {matches.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>No active users match "{pickerQuery}"</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                    {matches.map(u => {
                      const isSelected = selectedIds.includes(u.id);
                      return (
                        <button key={u.id} onClick={() => {
                          const newIds = isSelected
                            ? selectedIds.filter(id => id !== u.id)
                            : [...selectedIds, u.id];
                          updateStage(idx, 'specific_owner_ids', newIds);
                        }} className="btn" style={{
                          fontSize: 12, padding: '6px 12px', fontWeight: 600,
                          backgroundColor: isSelected ? '#D4A843' : '#f3f4f6',
                          color: isSelected ? '#1a1a2e' : '#6b7280',
                          border: isSelected ? '1px solid #D4A843' : '1px solid #e5e7eb',
                        }}>
                          {u.full_name || u.username}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p style={{ fontSize: 11, color: '#9ca3af', margin: '8px 0 0' }}>
                  {selectedIds.length === 0
                    ? 'Nobody picked yet — pick at least one person.'
                    : `${selectedIds.length} picked — only ${selectedIds.length === 1 ? 'this person owns' : 'these people own'} the step.`}
                </p>
              </div>
            );
          })()}
          </React.Fragment>
        ))}
      </div>

      {/* Transition Matrix */}
      {stages.filter(s => s.id).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Allowed Transitions</h3>
          <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>Click to toggle which stage transitions are allowed</p>
          <div className="table-wrapper">
            <table className="data-table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>From ↓ / To →</th>
                  {stages.filter(s => s.id).map(s => (
                    <th key={s.id} style={{ textAlign: 'center' }}>
                      <span style={{ color: s.color, fontWeight: 700 }}>{s.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stages.filter(s => s.id && s.stage_type !== 'terminal').map(from => (
                  <tr key={from.id}>
                    <td style={{ fontWeight: 600, color: from.color }}>{from.name}</td>
                    {stages.filter(s => s.id).map(to => {
                      if (from.id === to.id) return <td key={to.id} style={{ textAlign: 'center', backgroundColor: '#f3f4f6' }}>—</td>;
                      const isAllowed = transitions.some(t => t.from_stage_id === from.id && t.to_stage_id === to.id);
                      return (
                        <td key={to.id} style={{ textAlign: 'center' }}>
                          <button
                            onClick={() => toggleTransition(from.id, to.id)}
                            style={{
                              width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer',
                              backgroundColor: isAllowed ? '#10b981' : '#f3f4f6',
                              color: isAllowed ? '#fff' : '#d1d5db',
                              fontWeight: 700, fontSize: 14,
                            }}
                          >
                            {isAllowed ? '✓' : '·'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transition Gates — required fields to move along each allowed edge.
           Per-edge (stronger than the wizard's one-gate-per-stage): Approve
           can demand different fields than Deny. FormSchema gates edit with
           the same FormBuilder the wizard embeds; legacy flat-array gates
           (Refund etc.) edit as raw JSON so live workflows aren't disturbed. */}
      {transitions.filter(t => t.from_stage_id && t.to_stage_id).length > 0 && (() => {
        const stageById = (sid) => stages.find(s => s.id === sid);
        return (
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Transition Gates</h3>
            <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>
              Fields someone must fill in to move an item along that edge. No gate = one-click move.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {transitions.map((t, ti) => {
                const from = stageById(t.from_stage_id);
                const to = stageById(t.to_stage_id);
                if (!from || !to) return null;
                const rf = t.required_fields;
                const isLegacyArray = Array.isArray(rf);
                const count = isLegacyArray ? rf.length : gateFieldCount(rf);
                const isOpen = expandedGate === ti;
                return (
                  <div key={`${t.from_stage_id}-${t.to_stage_id}`} style={{
                    backgroundColor: '#fff', borderRadius: 10, border: isOpen ? '1px solid #D4A843' : '1px solid #e5e7eb',
                  }}>
                    <button
                      onClick={() => setExpandedGate(isOpen ? null : ti)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 600, color: from.color }}>{from.name}</span>
                      <span style={{ fontSize: 13, color: '#9ca3af' }}>→</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: to.color }}>{to.name}</span>
                      <span style={{
                        marginLeft: 'auto', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                        backgroundColor: count > 0 ? '#ecfdf5' : '#f3f4f6',
                        color: count > 0 ? '#047857' : '#9ca3af',
                      }}>
                        {count > 0 ? `${count} field${count === 1 ? '' : 's'} required${isLegacyArray ? ' (legacy)' : ''}` : 'No gate'}
                      </span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{isOpen ? '▾' : '▸'}</span>
                    </button>
                    {isOpen && (
                      <div style={{ padding: '0 16px 16px' }}>
                        {isLegacyArray ? (
                          <GateJsonEditor value={rf} onSave={(parsed) => updateTransitionGate(ti, parsed)} />
                        ) : (
                          <>
                            <FormBuilder
                              value={rf && Array.isArray(rf.sections) ? rf : EMPTY_GATE_SCHEMA}
                              onChange={(next) => updateTransitionGate(ti, next)}
                            />
                            {count > 0 && (
                              <button className="btn" onClick={() => {
                                if (window.confirm('Remove this gate? The transition becomes a one-click move.')) {
                                  updateTransitionGate(ti, null);
                                }
                              }} style={{ marginTop: 8, fontSize: 12, color: '#dc2626', backgroundColor: '#fef2f2', border: '1px solid #fecaca', fontWeight: 600 }}>
                                Clear gate
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
